import React from 'react';
import AcknowledgeIllustration from '../../../../assets/svg/acknowledge-illustration.svg';
import { Textarea } from '@/shared/components/TextField';
import { ACKNOWLEDGE_SR_LABELS as L } from '../../constants/labels';
import {
  formatScheduledDate,
  formatScheduledTime,
} from './viewServiceRequestHelpers';

interface AcknowledgeServiceRequestModalProps {
  variant: 'accepted' | 'rejected';
  open: boolean;
  onClose: () => void;
  scheduledStartAt: string | null | undefined;
  scheduledEndAt: string | null | undefined;
  /** Reject-only — current textarea value */
  reason?: string;
  /** Reject-only — textarea change handler */
  onReasonChange?: (value: string) => void;
  /** Reject-only — validation error shown below the textarea */
  reasonError?: string | null;
  /** Whether the confirm button can be clicked */
  canSubmit?: boolean;
  isSubmitting: boolean;
  /** Inline error shown above the buttons (e.g. 409 window-expired message) */
  submitError: string | null;
  onSubmit: () => void;
}

/**
 * Combined accept / reject confirmation modal for the technician acknowledge flow.
 *
 * When `variant="accepted"` — shows title, scheduled window body, and confirm button.
 * When `variant="rejected"` — adds a helper paragraph and a required reason textarea
 * (max 500 chars with live counter).
 *
 * Both variants disable all interactive elements during submission to prevent
 * double-submits. Follows the layout and button style of {@link CancelServiceRequestModal}.
 */
const AcknowledgeServiceRequestModal: React.FC<
  AcknowledgeServiceRequestModalProps
> = ({
  variant,
  open,
  onClose,
  scheduledStartAt,
  scheduledEndAt,
  reason = '',
  onReasonChange,
  reasonError,
  canSubmit = true,
  isSubmitting,
  submitError,
  onSubmit,
}) => {
  if (!open) {
    return null;
  }

  const isAccept = variant === 'accepted';

  const formatWindowDate = (iso: string | null | undefined): string => {
    const date = formatScheduledDate(iso);
    const time = formatScheduledTime(iso);
    if (date === '—') {
      return '—';
    }
    return `${date}, ${time}`;
  };

  const title = isAccept ? L.ACCEPT_TITLE : L.REJECT_TITLE;
  const bodyPrefix = isAccept ? L.ACCEPT_BODY_PREFIX : L.REJECT_BODY_PREFIX;
  const formattedStart = formatWindowDate(scheduledStartAt);
  const formattedEnd = formatWindowDate(scheduledEndAt);

  const confirmLabel = isSubmitting
    ? isAccept
      ? L.ACCEPT_BUTTON_SUBMITTING
      : L.REJECT_BUTTON_SUBMITTING
    : isAccept
      ? L.ACCEPT_BUTTON_CONFIRM
      : L.REJECT_BUTTON_CONFIRM;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="acknowledge-modal-title"
    >
      <div className="mx-4 flex w-full max-w-[412px] flex-col gap-6 rounded-2xl bg-white p-6 shadow-xl">
        {/* Illustration */}
        <div className="flex justify-center">
          <AcknowledgeIllustration
            aria-hidden="true"
            width={137}
            height={137}
          />
        </div>

        {/* Title */}
        <p
          id="acknowledge-modal-title"
          className="text-center text-[#474A4C]"
          style={{ fontSize: 18, fontWeight: 400, lineHeight: '26px' }}
        >
          {title}
        </p>

        {/* Body — prefix/connector in #474A4C, dates in #181A1B */}
        <p
          className="text-center tracking-[0.25px]"
          style={{ fontSize: 14, lineHeight: '20px', color: '#474A4C' }}
        >
          <span>{bodyPrefix}</span>
          <span style={{ color: '#181A1B' }}>{formattedStart}</span>
          <span>{L.ACCEPT_BODY_TO}</span>
          <span style={{ color: '#181A1B' }}>{formattedEnd}?</span>
        </p>

        {/* Reject-only: textarea with floating label, error icon, char count */}
        {!isAccept && (
          <Textarea
            label={L.FIELD_REASON}
            value={reason}
            onChange={(e) =>
              onReasonChange?.((e.target as HTMLTextAreaElement).value ?? '')
            }
            maxLength={L.REASON_MAX_LENGTH}
            rows={4}
            fullWidth
            error={!!reasonError}
            {...(reasonError != null && { errorMessage: reasonError })}
            disabled={isSubmitting}
          />
        )}

        {/* Inline submit error */}
        {submitError && (
          <p className="text-center text-bodySmall text-[#E74141]">
            {submitError}
          </p>
        )}

        {/* Buttons — accept: cancel(140) + confirm(flex-1); reject: both 140px centered */}
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="h-10 w-35 shrink-0 rounded-lg border border-[#DCDDE0] px-4 text-sm font-medium tracking-[0.1px] text-[#474A4C] transition-colors hover:bg-neutral-95 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {L.BUTTON_CANCEL}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting || !canSubmit}
            className={`h-10 rounded-lg px-4 text-sm font-medium tracking-[0.1px] text-white whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              isAccept
                ? 'flex-1 bg-[#2DBA8D] hover:bg-[#27a67d]'
                : 'min-w-35 shrink-0 bg-[#E74141] hover:bg-[#d03a3a]'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcknowledgeServiceRequestModal;
